import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { blogs, events, publications, lectureSeries, researchProjects, phdPrograms, courseOfferings } from '../data/mockData';

// Define the search result types
export interface SearchResult {
  id: string | number;
  title: string;
  category: string;
  url: string;
  isExternalLink?: boolean; // Flag for external links
  description?: string;
  date?: string;
  scrollTo?: string; // CSS selector for scrolling to specific content
}

interface SearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  loading: boolean;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dynamicContent, setDynamicContent] = useState<SearchResult[]>([]);

  // Function to extract content from the current page
  const extractPageContent = useCallback((): SearchResult[] => {
    const pageResults: SearchResult[] = [];
    
    try {
      // Get current page path
      const currentPath = window.location.pathname;
      
      // Get page title
      const pageTitle = document.title;
      
      // Get main headings
      const mainHeadings = Array.from(document.querySelectorAll('h1, h2')).map(el => el.textContent || '');
      
      // Get page content 
      const contentElements = Array.from(document.querySelectorAll('main p, main h3, main h4, main li'));
      const pageContent = contentElements.map(el => el.textContent || '').filter(Boolean).join(' ');
      
      // Add current page to results if we got content
      if (pageContent) {
        pageResults.push({
          id: `page-${currentPath.replace(/\//g, '-')}`,
          title: `Page: ${pageTitle}`,
          category: 'Current Page',
          url: currentPath,
          description: mainHeadings.join(' - ') || pageContent.substring(0, 150) + '...',
        });
        
        // Extract sections with headings
        const sections = Array.from(document.querySelectorAll('main section, main article, main div[id]'));
        sections.forEach((section, index) => {
          const sectionId = section.id;
          const sectionHeading = section.querySelector('h2, h3, h4')?.textContent;
          
          if (sectionHeading) {
            const sectionContent = Array.from(section.querySelectorAll('p, li'))
              .map(el => el.textContent || '')
              .filter(Boolean)
              .join(' ');
            
            if (sectionContent) {
              pageResults.push({
                id: `section-${sectionId || index}`,
                title: sectionHeading,
                category: 'Page Section',
                url: currentPath + (sectionId ? `#${sectionId}` : ''),
                description: sectionContent.substring(0, 150) + '...',
                scrollTo: sectionId ? `#${sectionId}` : undefined
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error extracting page content:', error);
    }
    
    return pageResults;
  }, []);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when search is open
    
    // Extract content from current page to include in search
    const pageContent = extractPageContent();
    
    // Save the extracted content to be used in search
    if (pageContent.length > 0) {
      setDynamicContent(pageContent);
    }
  }, [extractPageContent]);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    document.body.style.overflow = 'auto'; // Restore scrolling
  }, []);

  // Function to transform data into searchable items
  const transformData = useCallback((): SearchResult[] => {
    const allResults: SearchResult[] = [];

    // Add main pages with their titles and headings
    const mainPages = [
      { id: 'home', title: 'Home', category: 'Page', url: '/', description: 'Centre for Human Sciences at Rishihood University' },
      { id: 'about', title: 'About', category: 'Page', url: '/about', description: 'About the Centre for Human Sciences' },
      { id: 'research', title: 'Research', category: 'Page', url: '/research-projects', description: 'Research Projects and Initiatives' },
      { id: 'courses', title: 'Courses', category: 'Page', url: '/courses', description: 'Academic Courses and Programs' },
      { id: 'internships', title: 'Internships', category: 'Page', url: '/internships', description: 'Internship Opportunities' },
      { id: 'phd', title: 'PhD Programs', category: 'Page', url: '/phd', description: 'Doctoral Studies and Research' },
      { id: 'publications', title: 'Publications', category: 'Page', url: '/publications', description: 'Academic Publications and Research Papers' },
      { id: 'blogs', title: 'Blogs', category: 'Page', url: '/blogs', description: 'Latest Articles and Insights' },
      { id: 'events', title: 'Events', category: 'Page', url: '/events', description: 'Upcoming and Past Events' },
      { id: 'lecture-series', title: 'Lecture Series', category: 'Page', url: '/lecture-series', description: 'Distinguished Lectures and Talks' },
      { id: 'media-gallery', title: 'Media Gallery', category: 'Page', url: '/media-gallery', description: 'Photos and Videos' },
      { id: 'contact', title: 'Contact', category: 'Page', url: '/contact', description: 'Get in Touch with the Centre' },
    ];
    
    // Add commonly searched sections
    const commonSections = [
      // Home page sections
      {
        id: 'hero-section',
        title: 'Welcome to Centre for Human Sciences',
        category: 'Page Section - Home',
        url: '/#hero-section',
        description: 'Welcome message and introduction to the Centre for Human Sciences'
      },
      {
        id: 'home-course-offering',
        title: 'Course Offerings',
        category: 'Page Section - Home',
        url: '/#home-course-offering',
        description: 'Discover our specialized courses focused on Indian knowledge systems'
      },
      
      {
        id: 'featured-research',
        title: 'Featured Research',
        category: 'Page Section - Home',
        url: '/#featured-research',
        description: 'Highlighted research projects from the Centre for Human Sciences'
      },
     
      {
        id: 'upcoming-events',
        title: 'Upcoming Events',
        category: 'Page Section - Home',
        url: '/#upcoming-events',
        description: 'Upcoming seminars, conferences, and lectures at the Centre'
      },
      {
        id: 'home-lecture-series',
        title: 'Lecture Series',
        category: 'Page Section - Home',
        url: '/#home-lecture-series',
        description: `Explore our distinguished ICPR-sponsored lecture series on Sri Aurobindo's Philosophy, Nationalism and Swaraj.`
      },
      // {
      //   id: 'latest-blogs',
      //   title: 'Latest Blogs',
      //   category: 'Page Section - Home',
      //   url: '/#latest-blogs',
      //   description: 'Recent blog posts from the Centre for Human Sciences'
      // },
      // Contact page sections
      { 
        id: 'faq', 
        title: 'Frequently Asked Questions', 
        category: 'Page Section - Contact', 
        url: '/contact#faq', 
        description: 'Common questions about the Centre, collaborations, student opportunities, and events'
      },
      { 
        id: 'contact-info', 
        title: 'Contact Information', 
        category: 'Page Section - Contact', 
        url: '/contact', 
        description: 'Address, phone, email and office hours of the Centre for Human Sciences'
      },
      // Event tabs
      {
        id: 'conferences-tab',
        title: 'Conferences & Seminars',
        category: 'Tab - Events',
        url: '/events/conferences',
        description: 'Academic conferences and seminars organized by the Centre for Human Sciences'
      },
      {
        id: 'centre-events-tab',
        title: 'Events at the Centre',
        category: 'Tab - Events',
        url: '/events/centre',
        description: 'Events held at the Centre for Human Sciences'
      },
      {
        id: 'media-events-tab',
        title: 'In the Media',
        category: 'Tab - Events',
        url: '/events/media',
        description: 'Media appearances and coverage of the Centre\'s activities'
      },
      // Publication tabs
      {
        id: 'journals-tab',
        title: 'Publications',
        category: 'Tab - Publications',
        url: '/publications/journals',
        description: 'Journal publications by the Centre\'s researchers'
      },
      {
        id: 'books-tab',
        title: 'Reports',
        category: 'Tab - Publications',
        url: '/publications/reports',
        description: 'Research reports published by the Centre'
      },
      {
        id: 'articles-tab',
        title: 'Books',
        category: 'Tab - Publications',
        url: '/publications/books',
        description: 'Books authored by the Centre\'s faculty and researchers'
      },
      // About tabs
      {
        id: 'about-advisory-board-tab',
        title: 'Our Advisory Board',
        category: 'Tab - About',
        url: '/about/advisory-board',
        description: 'Information about Advisory Board of the Centre for Human Sciences'
      },
      {
        id: 'about-internship-advisory-tab',
        title: 'Internship Advisory Committee',
        category: 'Tab - About',
        url: '/about/internship-advisory',
        description: 'Information about Internship Advisory Committee of the Centre for Human Sciences'
      },
      {
        id: 'vision-directors-note-tab',
        title: 'Directors Note',
        category: 'Tab - About',
        url: '/about/directors-note',
        description: "Director's note of the Centre for Human Sciences"
      },
      {
        id: 'about-research-advisory-tab',
        title: 'Research Advisory Committee',
        category: 'Tab - About',
        url: '/about/research-advisory',
        description: 'Information about Research Advisory Committee of the Centre for Human Sciences'
      },
      {
        id: 'about-rishihood-tab',
        title: 'About Rishihood',
        category: 'Tab - About',
        url: '/about/rishihood',
        description: 'Information about Rishihood University'
      },
      {
        id: 'about-chs-tab',
        title: 'About CHS',
        category: 'Tab - About',
        url: '/about/chs',
        description: 'Information about the Centre for Human Sciences'
      },
      {
        id: 'vision-mission-tab',
        title: 'Vision & Mission',
        category: 'Tab - About',
        url: '/about/vision-mission',
        description: 'The vision and mission of the Centre for Human Sciences'
      },
      {
        id: 'team-tab',
        title: 'Our Team',
        category: 'Tab - About',
        url: '/about/team',
        description: 'Faculty and staff of the Centre for Human Sciences'
      },
    ];
    
    allResults.push(...mainPages);
    allResults.push(...commonSections);

    // Add blogs
    // blogs.forEach(blog => {
    //   allResults.push({
    //     id: blog.id,
    //     title: blog.title,
    //     category: 'Blog',
    //     url: `/blogs/${blog.slug || blog.id}`,
    //     description: blog.excerpt,
    //     date: blog.date,
    //     scrollTo: `#blog-${blog.id}`
    //   });
    // });

    // Add events
    events.forEach(event => {
      const isExternal = !!event.registrationLink;
      allResults.push({
        id: event.id,
        title: event.title,
        category: 'Event',
        url: isExternal ? event.registrationLink : `/events/${event.slug || event.id}`,
        isExternalLink: isExternal,
        description: event.description,
        date: typeof event.date === 'string' ? event.date : `${event.date?.day} ${event.date?.month}`,
        scrollTo: !isExternal ? `#event-${event.id}` : undefined
      });
    });

    // Add publications
    Object.entries(publications).forEach(([category, items]) => {
      items.forEach((pub: any) => {
        const externalLink = pub.url || '';
        allResults.push({
          id: pub.id,
          title: pub.title,
          category: `Publication (${category})`,
          url: externalLink || `/publications/${category}/${pub.id}`,
          isExternalLink: !!externalLink,
          description: pub.authors && Array.isArray(pub.authors) ? pub.authors.join(', ') : pub.description || ''
        });
      });
    });

    // Add lecture series
    lectureSeries.forEach(lecture => {
      // Add the upcoming lecture if available
      if (lecture.upcoming) {
        allResults.push({
          id: `${lecture.id}-upcoming`,
          title: lecture.upcoming.title || lecture.title,
          category: 'Upcoming Lecture',
          url: `/lecture-series/${lecture.id}`,
          description: `Speaker: ${lecture.upcoming.speaker}, ${lecture.upcoming.affiliation}`,
          date: lecture.upcoming.date,
          scrollTo: `#lecture-${lecture.id}-upcoming`
        });
      }
      
      // Add past lectures if available
      if (lecture.past && lecture.past.length > 0) {
        lecture.past.forEach((pastLecture, index) => {
          allResults.push({
            id: `${lecture.id}-past-${index}`,
            title: pastLecture.title,
            category: 'Past Lecture',
            url: `/lecture-series/${lecture.id}`,
            description: `Speaker: ${pastLecture.speaker}, ${pastLecture.affiliation}`,
            date: pastLecture.date,
            scrollTo: `#lecture-${lecture.id}-past-${index}`
          });
        });
      }
    });

    // Add research projects
    researchProjects.forEach(project => {
      allResults.push({
        id: project.id,
        title: project.title,
        category: 'Research',
        url: `/research-projects/${project.slug || project.id}`,
        description: project.description,
        scrollTo: `#research-${project.id}`
      });
    });

    // Add PhD programs
    // phdPrograms.forEach(program => {
    //   allResults.push({
    //     id: program.id,
    //     title: program.title,
    //     category: 'PhD Program',
    //     url: `/phd/${program.id}`,
    //     description: program.description,
    //     scrollTo: `#phd-${program.id}`
    //   });
    // });

    // Add courses
    courseOfferings.forEach(course => {
      const isExternal = !!course.link;
      allResults.push({
        id: course.id,
        title: course.title,
        category: 'Course',
        url: isExternal ? course.link : `/courses/${course.id}`,
        isExternalLink: isExternal,
        description: course.description,
        scrollTo: !isExternal ? `#course-${course.id}` : undefined
      });
    });

    // Add the dynamic content from the current page
    allResults.push(...dynamicContent);

    return allResults;
  }, [dynamicContent]);

  // Function to perform search
  const performSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    
    const allData = transformData();
    const lowerQuery = query.toLowerCase();
    
    // Search in title, description, etc.
    const results = allData.filter(item => 
      item.title?.toLowerCase().includes(lowerQuery) || 
      (item.description && item.description.toLowerCase().includes(lowerQuery)) ||
      item.category.toLowerCase().includes(lowerQuery)
    );
    
    // Sort results - titles matching the query first
    results.sort((a, b) => {
      const aInTitle = a.title?.toLowerCase().includes(lowerQuery);
      const bInTitle = b.title?.toLowerCase().includes(lowerQuery);
      
      if (aInTitle && !bInTitle) return -1;
      if (!aInTitle && bInTitle) return 1;
      return 0;
    });
    
    setSearchResults(results);
    setLoading(false);
  }, [transformData]);

  // Update search results when query changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300); // Debounce search to avoid too many updates

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, performSearch]);

  // Listen for escape key to close search
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSearchOpen) {
        closeSearch();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isSearchOpen, closeSearch]);

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        searchResults,
        isSearchOpen,
        openSearch,
        closeSearch,
        loading
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};